import { query } from '@warp-drive/utilities/json-api';
import { executeSearchRequest } from 'tio-data/requests/search';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import type { Store as StoreService } from '@warp-drive/core';
import type { LpnPageExtension as LpnPage } from 'tio-data/data/resources/lpn-page.ext';
import type { OpenSearchResponse, TasUnifiedInstitution } from 'tio-data/data/resources/search.ext';
import type PartnerService from 'tio-employee/services/partner';

/**
 * The LPN identifier for a school search result: its own `identifier` when that is one, else
 * `dapip_<id>` for an accredited school or `tas_<id>` for an unaccredited one, or null when the
 * result carries neither id.
 */
export function lpnIdentifierFor(institution: TasUnifiedInstitution) {
  if (/^(dapip|tas)_/.test(institution.identifier ?? '')) return institution.identifier;
  if (institution.dapipId) return `dapip_${institution.dapipId}`;
  if (institution.institutionId) return `tas_${institution.institutionId}`;
  return null;
}

/**
 * Whether the company offers an LPN discount at the chosen school: LPN is on, the school has an
 * LPN page, and the company has not switched that page off.
 *
 * Side effect: requests the school's LPN page with its company settings.
 */
export async function isOfferedLpnSchool(
  store: StoreService,
  companySettings: PartnerService['companySettings'] | undefined,
  institution: TasUnifiedInstitution
) {
  const lpn = companySettings?.lpn;
  if (!(lpn && 'isEnabled' in lpn && lpn.isEnabled)) return false;

  const identifier = lpnIdentifierFor(institution);
  if (!identifier) return false;

  const { content } = (await store.request(
    query<LpnPage>('lpn-page', {
      'filter[institution-identifier]': identifier,
      include: 'company-lpn-settings,company-lpn-settings.company-setting',
    })
  )) as { content: { data: LpnPage[] } };
  const page = content.data[0];
  if (!page) return false;
  const setting = await page.settingForCompany(companySettings.id);
  return !setting || setting.isEnabled;
}

/**
 * The school search result for an LPN identifier, as the program's school search returns it, so a
 * school picked from the LPN drawer is saved under its official name and ids.
 *
 * Side effect: runs a school search scoped to the program template.
 *
 * @returns null when the program does not allow that school
 * @throws when the search request fails
 */
export async function institutionForLpnIdentifier(
  store: StoreService,
  programTemplateId: string,
  identifier: string
) {
  // A school's search document id is its LPN identifier.
  const search = new OpenSearchQueryBuilder()
    .setPagination(1, 1)
    .addFilter({ term: { _id: identifier } });
  const { content } = await store.request<OpenSearchResponse<TasUnifiedInstitution>>(
    executeSearchRequest({
      'model-type': 'TasUnifiedInstitution',
      'search-body': search.build(),
      'program-template-id': programTemplateId,
    })
  );
  const documents = new OpenSearchResponseBuilder(content).getDocuments();
  return documents.find((doc) => doc.identifier === identifier) ?? null;
}
